import { inject, injectable } from 'inversify'
import { nanoid } from 'nanoid'
import { Group } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import type { AuxiliaryMouseHelper } from '@/plugins/cad/module/CADModule/auxiliaryTool/auxiliaryMouseHelper'
import {
  type MouseTipHelper,
  MouseTipId,
} from '@/plugins/cad/module/CADModule/auxiliaryTool/MouseTipHelper'
import type { CADController } from '@/plugins/cad/module/CADModule/CADController'
import { DrawPolylineCommand } from '@/plugins/cad/module/CADModule/command/DrawPolylineCommand'
import type { CADViewManager } from '@/plugins/cad/module/CADModule/manager/CADViewManager'
import type { HistoryManager } from '@/plugins/cad/module/CADModule/manager/HistoryManager'
import type { IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { PolylineEntity } from '@/plugins/cad/module/CADModule/model/entity/PolylineEntity'
import { createCadLine2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import { MaterialUtils } from '@/plugins/cad/module/CADModule/object/material/MaterialUtils'
import type { EntitySelector } from '@/plugins/cad/module/CADModule/selector/entitySelector'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'

const polylineMachine = setup({
  actions: {
    // 声明一个占位动作
    entrySpecifyStartPoint: () => {},
    entrySpecifyNextPoint: () => {},
  },
}).createMachine({
  id: 'drawPolylineTool',
  initial: 'idle',
  states: {
    idle: { on: { NEXT: 'specifyStartPoint' } },
    specifyStartPoint: {
      entry: { type: 'entrySpecifyStartPoint' },
      on: {
        NEXT: {
          target: 'specifyNextPoint',
        },
      },
    },
    specifyNextPoint: {
      entry: { type: 'entrySpecifyNextPoint' },
      on: {
        NEXT: {},
      },
    },
  },
})

/**
 * 多段线绘制工具，连续拾取顶点组成开放折线。
 */
@injectable()
export class DrawPolylineTool
  extends StateMachineTool<typeof polylineMachine>
  implements MouseEventInfoHandler, KeyEventInfoHandler
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawPolylineTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = polylineMachine.provide({
    actions: {
      entrySpecifyStartPoint: () => this.entrySpecifyStartPoint(),
      entrySpecifyNextPoint: () => this.entrySpecifyNextPoint(),
    },
  })
  /** 所属控制器，由框架自动注入 */
  declare controller: CADController

  private tempPoint: IPoint | null = null
  private group = new Group()
  private lineObject: Line2 | null = null
  private dimensionGroup: LineDimensionGroup | null = null
  private entity: PolylineEntity | null = null
  private currentCommand: DrawPolylineCommand | null = null

  private get points() {
    return (this.entity?.vertices as IPoint[]) || []
  }

  protected onInitialize(entityData?: Partial<PolylineEntity>) {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawPolylineCommand(
      this.entity,
      this.viewManager,
      this.updateCanvas.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate() {
    // 退出前创建图元
    this.historyManager.commitCommand()
    this.lineObject && DrawUtils.disposeObject(this.lineObject)
    this.dimensionGroup?.dispose()
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.tempPoint = null
    this.lineObject = null
    this.dimensionGroup = null
    this.entity = null
    this.currentCommand = null
  }

  /**
   * 鼠标按下：左键追加顶点，右键结束绘制。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      this.currentCommand?.addPoint(
        this.tempPoint || { x: params.worldPos.x, y: params.worldPos.y }
      )
      this.historyManager.refresh()
      this.send({ type: 'NEXT' })
    } else if (params.event.button === 2) {
      // 右键
      this.exit()
    }
  }

  /**
   * 鼠标移动：更新末端预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新多段线标注 */
  onViewerUpdate() {
    this.dimensionGroup?.update()
  }

  // 正交/捕捉/极轴/点云 统一处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }

    const startPoint = this.points.length > 0 ? this.points[this.points.length - 1] : undefined
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, {
      startPoint,
      entities: this.entity ? [this.entity] : undefined,
    })

    this.tempPoint = {
      x: finalPoint.x,
      y: finalPoint.y,
    }
  }

  /************ 状态机状态处理 ************/
  private entrySpecifyStartPoint() {
    this.mouseTipHelper.addTip(MouseTipId.Draw, CadTipKey.draw.firstPoint)
  }

  private entrySpecifyNextPoint() {
    this.mouseTipHelper.addTip(MouseTipId.Draw, CadTipKey.draw.nextPoint)
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<PolylineEntity>): PolylineEntity {
    const entity = new PolylineEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
      vertices: [],
      isClosed: false,
    })
    return entity
  }

  /************ 渲染 ************/
  private getDrawPoints() {
    return [...this.points].concat(this.tempPoint ? [this.tempPoint] : [])
  }
  private drawLineObject() {
    const points = this.getDrawPoints()
    if (!this.entity || points.length < 2) {
      return
    }
    const geometry = DrawUtils.getLineGeometryByPoints(points)
    const materialOption = MaterialUtils.getMaterialOptionByEntity(this.entity)
    const material = MaterialUtils.createMaterialByLineType(this.entity.lineType, materialOption)
    this.lineObject = createCadLine2Object(geometry, material)
    this.lineObject.computeLineDistances()
    this.group.add(this.lineObject)
  }

  private updateLineObject() {
    if (!this.lineObject) {
      this.drawLineObject()
      return
    }

    const points = this.getDrawPoints()
    this.lineObject.geometry.dispose()
    this.lineObject.geometry = DrawUtils.getLineGeometryByPoints(points)
    this.lineObject.computeLineDistances()
  }

  private drawDimension() {
    const startPoint = this.points[this.points.length - 1]
    const endPoint = this.tempPoint
    if (!startPoint || !endPoint) {
      return
    }
    if (this.dimensionGroup) {
      this.dimensionGroup.updateLine(startPoint, endPoint)
      return
    }
    this.dimensionGroup = new LineDimensionGroup(
      startPoint,
      endPoint,
      {},
      this.group,
      this.controller.domElement!.parentElement!,
      (point: IPoint) => this.controller.getScreenPosition(point),
      (radius: number) => this.controller.getPixelSizeInWorld(radius),
      this.unitPresentation
    )
  }

  private updateCanvas() {
    this.updateLineObject()
    this.drawDimension()
  }
}
